import { getDatabase, ref, push, set, update, remove, get, onValue, query, orderByChild, equalTo } from "firebase/database"
import { getAuth } from 'firebase/auth'
import { getFirestore, collection, doc, getDoc, getDocs, setDoc, deleteDoc, runTransaction, Timestamp } from 'firebase/firestore'
import { inject } from 'vue'
import { FILE_EXTENSIONS, SUPPORTED_DOCUMENT_TYPES } from '../constants/fileTypes'
import { FirebaseStorageService } from './FirebaseStorageService'
import { RequestService } from './RequestService'
import { handleErrors } from '../utils/errorHandler'
import {
  getContentLibraryOriginalPath,
  getContentLibraryTemplatePath,
  buildContentLibraryDownloadFileName,
  normalizeContentLibraryEmbedUrl,
  toContentLibraryDate
} from '../utils/contentLibraryHelpers'

const DOCUMENT_DOWNLOAD_CONTENT_TYPES = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  csv: 'text/csv',
  txt: 'text/plain'
}

const BOGOTA_TZ = 'America/Bogota'

// Dates in the per-user Firestore subcollections (contentLibraryFavorites /
// contentLibraryDownloads) are Firestore Timestamps. Legacy docs still hold ISO
// strings, so re-written fields are converted rather than carried over as-is —
// that way a doc self-heals the next time it is touched. Realtime Database
// writes keep using ISO strings: RTDB has no Timestamp type.
const toFirestoreTimestamp = (value) => {
  const date = toContentLibraryDate(value)
  return date ? Timestamp.fromDate(date) : null
}

export const ContentLibraryService = () => {
  const db = getDatabase()
  const auth = getAuth()
  const firestore = getFirestore()
  const { uploadDocument, deleteDocument } = FirebaseStorageService()
  const { post, createLog } = RequestService()
  const functionsBaseUrl = inject('functionsBaseUrl', '')
  const pdfExtensions = new Set(SUPPORTED_DOCUMENT_TYPES.PDF.map((mimeType) => FILE_EXTENSIONS[mimeType]?.replace('.', '')).filter(Boolean))
  const wordExtensions = new Set(SUPPORTED_DOCUMENT_TYPES.WORD.map((mimeType) => FILE_EXTENSIONS[mimeType]?.replace('.', '')).filter(Boolean))
  const excelExtensions = new Set(SUPPORTED_DOCUMENT_TYPES.EXCEL.map((mimeType) => FILE_EXTENSIONS[mimeType]?.replace('.', '')).filter(Boolean))

  const mapSnapshotToArray = (snapshot) => {
    const value = snapshot.val() || {}
    return Object.entries(value).map(([id, data]) => ({ id, ...data }))
  }

  const getCurrentUserUid = () => {
    return auth.currentUser?.uid || ''
  }

  const getNormalizedAgencyId = (userData = {}) => {
    return userData.agencyId || userData.agency_id || ''
  }

  const getNormalizedTeamMemberName = ({ authUser, userData } = {}) => {
    const fullName = [userData?.first_name || userData?.firstName || '', userData?.last_name || userData?.lastName || '']
      .filter(Boolean)
      .join(' ')
      .trim()

    return fullName || userData?.name || userData?.displayName || authUser?.displayName || authUser?.email || ''
  }

  const getNormalizedTitle = (userData = {}) => {
    return userData.title || userData.jobTitle || userData.role || ''
  }

  const getFileExtension = (value = '') => {
    const normalizedValue = String(value || '').toLowerCase().split('?')[0].split('#')[0]
    const segments = normalizedValue.split('.')
    return segments.length > 1 ? segments.pop() : ''
  }

  const getDownloadActivityDescription = (item = {}) => {
    const fileExtension = getFileExtension(item.documentFileName || item.libraryFileName || '')

    if (pdfExtensions.has(fileExtension)) {
      return 'PDF File Downloaded'
    }

    if (wordExtensions.has(fileExtension)) {
      return 'Word File Downloaded'
    }

    if (excelExtensions.has(fileExtension)) {
      return 'Excel File Downloaded'
    }

    if (['ppt', 'pptx', 'pps', 'ppsx'].includes(fileExtension)) {
      return 'PPT File Downloaded'
    }

    return 'File Downloaded'
  }

  const getCurrentLibraryActivityContext = async () => {
    try {
      const userUid = getCurrentUserUid()
      if (!userUid) {
        return null
      }

      const authUser = auth.currentUser || null
      const userDocRef = doc(firestore, 'users', userUid)
      const userSnapshot = await getDoc(userDocRef)
      const userData = userSnapshot.exists() ? userSnapshot.data() : {}

      return {
        userId: userUid,
        agencyId: getNormalizedAgencyId(userData),
        teamMemberName: getNormalizedTeamMemberName({ authUser, userData }),
        title: getNormalizedTitle(userData)
      }
    } catch (error) {
      handleErrors('ContentLibraryService.getCurrentLibraryActivityContext', error)
      throw error
    }
  }

  const getLibraryActivityDescription = (activityType, item = {}) => {
    if (activityType === 'file_downloaded') {
      return getDownloadActivityDescription(item)
    }

    if (activityType === 'favorite_added') {
      return 'Added to Favorites'
    }

    if (activityType === 'favorite_removed') {
      return 'Removed from Favorites'
    }

    return ''
  }

  const recordLibrarySearch = ({ term, trigger, itemId, typedTerm } = {}) => {
    const normalizedTerm = String(term || '').trim()
    if (!normalizedTerm || (trigger !== 'submit' && trigger !== 'suggestion')) {
      return
    }

    const body = { term: normalizedTerm, trigger }
    if (trigger === 'suggestion' && itemId) {
      body.itemId = String(itemId)
    }
    const normalizedTypedTerm = String(typedTerm || '').trim()
    if (trigger === 'suggestion' && normalizedTypedTerm) {
      body.typedTerm = normalizedTypedTerm
    }

    createLog({
      logName: 'content_library_activity_log_v1',
      eventId: 'CL_SEARCH',
      body
    }).catch((error) => {
      handleErrors('ContentLibraryService.recordLibrarySearch', error)
    })
  }

  const recordLastLibraryActivity = async ({ type, item } = {}) => {
    try {
      if (!type || !item?.id) {
        return null
      }

      const activityContext = await getCurrentLibraryActivityContext()
      if (!activityContext?.userId) {
        return null
      }

      const now = new Date().toISOString()
      const fileName = item.libraryFileName || item.documentDisplayName || item.documentFileName || ''
      const nextActivityData = {
        userId: activityContext.userId,
        type,
        activityDescription: getLibraryActivityDescription(type, item),
        itemId: item.id,
        fileName,
        taxonomyGroupId: item.taxonomyGroupId || '',
        libraryCategoryId: item.libraryCategoryId || '',
        agencyId: activityContext.agencyId || '',
        teamMemberName: activityContext.teamMemberName || '',
        title: activityContext.title || '',
        timestamp: now,
        updated_at: now
      }

      await update(ref(db, `CONTENT_LIBRARY/ITEMS/${item.id}`), {
        lastLibraryActivity: nextActivityData
      })

      return nextActivityData
    } catch (error) {
      handleErrors('ContentLibraryService.recordLastLibraryActivity', error)
      throw error
    }
  }

  const getAgencyLatestLibraryActivities = async (agencyId) => {
    try {
      if (!agencyId) {
        return []
      }

      const normalizedAgencyId = String(agencyId)

      return (await getItems())
        .map((item) => {
          const lastLibraryActivity = item.lastLibraryActivity || null
          if (!lastLibraryActivity?.timestamp) {
            return null
          }

          if (String(lastLibraryActivity.agencyId || '') !== normalizedAgencyId) {
            return null
          }

          return {
            ...lastLibraryActivity,
            userId: lastLibraryActivity.userId || '',
            itemId: lastLibraryActivity.itemId || item.id,
            fileName: lastLibraryActivity.fileName || item.libraryFileName || item.documentDisplayName || item.documentFileName || ''
          }
        })
        .filter(Boolean)
        .sort((firstActivity, secondActivity) => new Date(secondActivity.timestamp) - new Date(firstActivity.timestamp))
        .slice(0, 5)
    } catch (error) {
      handleErrors('ContentLibraryService.getAgencyLatestLibraryActivities', error)
      throw error
    }
  }

  const getFavoritesCollectionRef = () => {
    const userUid = getCurrentUserUid()
    if (!userUid) {
      return null
    }

    return collection(firestore, 'users', userUid, 'contentLibraryFavorites')
  }

  const getDownloadsCollectionRef = () => {
    const userUid = getCurrentUserUid()
    if (!userUid) {
      return null
    }

    return collection(firestore, 'users', userUid, 'contentLibraryDownloads')
  }

  // ============= ITEMS =============
  const getItems = async () => {
    try {
      const snapshot = await get(ref(db, 'CONTENT_LIBRARY/ITEMS'))
      return mapSnapshotToArray(snapshot)
    } catch (error) {
      handleErrors('ContentLibraryService.getItems', error)
      throw error
    }
  }

  const getItem = async (id) => {
    try {
      const snapshot = await get(ref(db, `CONTENT_LIBRARY/ITEMS/${id}`))
      const item = snapshot.val()
      return item ? { id, ...item } : null
    } catch (error) {
      handleErrors('ContentLibraryService.getItem', error)
      throw error
    }
  }

  const createItem = async (itemData) => {
    try {
      itemData.embedUrl = normalizeContentLibraryEmbedUrl(itemData.embedUrl) || null

      // Validation: if all fields are empty, don't save anything
      if (!itemData.libraryFileName && !itemData.description && !itemData.lawScope && !itemData.recipient && !itemData.documentFileName && !itemData.documentDisplayName && 
          !itemData.zoomUrl && !itemData.zoomPassword && !itemData.embedUrl && !itemData.libraryCategoryId) {
        return null // Return null for empty data (consistent with backend)
      }

      itemData.lawScope = itemData.lawScope || null
      itemData.recipient = itemData.recipient || null
      
      // Brandable template: upload the original to Storage on save (preview
      // only renders in memory). Persist brandingEngine and set cacheStatus so
      // the frontend can show a "Caching" badge immediately. RTDB strips null
      // fields on write, which would otherwise read back as undefined.
      if (itemData.brandingEngine) {
        if (!itemData.documentFile) {
          throw new Error('brandable_template_missing_file')
        }
        const templateDocumentFileName = itemData.documentFileName || itemData.documentFile.name
        const templateStoragePath = getContentLibraryTemplatePath(templateDocumentFileName)
        const uploadResult = await uploadDocument(itemData.documentFile, templateStoragePath)
        itemData.documentFileName = itemData.documentFileName || uploadResult.originalFileName
        itemData.cacheStatus = 'pending'
        itemData.cacheUpdatedAt = new Date().toISOString()
        delete itemData.cacheError
        delete itemData.documentFile
      }
      // Upload file to Firebase Storage if it exists (non-brandable flow).
      // Only `documentFileName` is persisted; the Storage path
      // is derived from it at download time by the backend, so promotion
      // across environments no longer requires re-signing URLs.
      else if (itemData.documentFile) {
        const directDocumentFileName = itemData.documentFileName || itemData.documentFile.name
        const directStoragePath = getContentLibraryOriginalPath(directDocumentFileName)
        const uploadResult = await uploadDocument(itemData.documentFile, directStoragePath)
        itemData.documentFileName = itemData.documentFileName || uploadResult.originalFileName

        if (!itemData.documentDisplayName) {
          itemData.documentDisplayName = itemData.documentFileName || uploadResult.originalFileName
        }

        delete itemData.documentFile
      }
      
      // Save to Realtime Database
      const newRef = push(ref(db, 'CONTENT_LIBRARY/ITEMS'))
      await set(newRef, {
        ...itemData,
        created_at: new Date().toISOString(),
        created_by: localStorage.getItem('userUid')
      })
      
      return { id: newRef.key, ...itemData }
    } catch (error) {
      handleErrors('ContentLibraryService.createItem', error)
      throw error
    }
  }

  const updateItem = async (id, itemData, shouldDeleteFile = false) => {
    try {
      if ('embedUrl' in itemData) {
        itemData.embedUrl = normalizeContentLibraryEmbedUrl(itemData.embedUrl) || null
      }

      itemData.lawScope = itemData.lawScope || null
      itemData.recipient = itemData.recipient || null

      // If file should be deleted (intentionally removed in UI)
      if (shouldDeleteFile) {
        const currentItem = await getItem(id)

        const storagePath = currentItem ? getItemStoragePath(currentItem) : ''
        if (storagePath) {
          try {
            await deleteDocument(storagePath)
          } catch (error) {
            handleErrors('ContentLibraryService.updateItem.deleteFile', error)
          }
        }

        itemData.documentFileName = null
        itemData.brandingEngine = null
        itemData.cacheStatus = null
        itemData.cacheError = null
        itemData.cacheUpdatedAt = null
        // Keep documentDisplayName if user wrote it manually
        if (!itemData.documentDisplayName) {
          itemData.documentDisplayName = null
        }
      }
      // Brandable template replacement: upload on save when a new file was
      // selected. Preview renders in memory only and never touches Storage.
      else if (itemData.brandingEngine) {
        const currentItem = await getItem(id)
        const promotedFromDirect = Boolean(currentItem && !currentItem.brandingEngine)
        const fileNameChanged = currentItem?.documentFileName !== itemData.documentFileName
        const templateFileReplaced = Boolean(itemData.documentFile)

        if (promotedFromDirect && !templateFileReplaced) {
          throw new Error('brandable_template_missing_file')
        }

        if (templateFileReplaced) {
          if (fileNameChanged || promotedFromDirect) {
            const storagePath = currentItem ? getItemStoragePath(currentItem) : ''
            if (storagePath) {
              try {
                await deleteDocument(storagePath)
              } catch (error) {
                handleErrors('ContentLibraryService.updateItem.deleteBrandingReplacement', error)
              }
            }
          }

          const templateDocumentFileName = itemData.documentFileName || itemData.documentFile.name
          const templateStoragePath = getContentLibraryTemplatePath(templateDocumentFileName)
          const uploadResult = await uploadDocument(itemData.documentFile, templateStoragePath)
          itemData.documentFileName = itemData.documentFileName || uploadResult.originalFileName
          delete itemData.documentFile

          itemData.cacheStatus = 'pending'
          itemData.cacheError = null
          itemData.cacheUpdatedAt = new Date().toISOString()
        } else {
          delete itemData.documentFile

          const brandingEngineChanged = currentItem?.brandingEngine !== itemData.brandingEngine
          if (brandingEngineChanged || fileNameChanged) {
            itemData.cacheStatus = 'pending'
            itemData.cacheError = null
            itemData.cacheUpdatedAt = null
          }
        }
      }
      // If there's a new file, delete the old one and upload the new one
      else if (itemData.documentFile) {
        const currentItem = await getItem(id)
        const demotedFromTemplate = Boolean(currentItem?.brandingEngine)

        const storagePath = currentItem ? getItemStoragePath(currentItem) : ''
        if (storagePath) {
          try {
            await deleteDocument(storagePath)
          } catch (error) {
            handleErrors('ContentLibraryService.updateItem.deleteOldFile', error)
          }
        }

        const directDocumentFileName = itemData.documentFileName || itemData.documentFile.name
        const directStoragePath = getContentLibraryOriginalPath(directDocumentFileName)
        const uploadResult = await uploadDocument(itemData.documentFile, directStoragePath)

        itemData.documentFileName = itemData.documentFileName || uploadResult.originalFileName

        // Smart logic for documentDisplayName
        if (!itemData.documentDisplayName ||
            itemData.documentDisplayName === currentItem?.documentFileName) {
          itemData.documentDisplayName = itemData.documentFileName || uploadResult.originalFileName
        }

        delete itemData.documentFile
        itemData.brandingEngine = null
        if (demotedFromTemplate) {
          itemData.cacheStatus = null
          itemData.cacheError = null
          itemData.cacheUpdatedAt = null
        }
      }
      
      // Extract updateDescription so it is not persisted on the item itself
      const updateDescription = itemData.updateDescription || null
      delete itemData.updateDescription

      const now = new Date().toISOString()
      const writePayload = {
        ...itemData,
        updated_at: now,
        updated_by: localStorage.getItem('userUid')
      }

      if (updateDescription) {
        writePayload.lastLibraryActivity = {
          type: 'item_updated',
          updateDescription,
          fileName: itemData.libraryFileName || '',
          updated_at: now
        }
      }

      // Update in Realtime Database
      await update(ref(db, `CONTENT_LIBRARY/ITEMS/${id}`), writePayload)

      // Maintain RECENT_UPDATES cache via server (RTDB path is read-only for clients)
      if (updateDescription && functionsBaseUrl) {
        post(
          'updateRecentUpdatesCache',
          `${functionsBaseUrl}/asyncJobsGateway/api/v1/content-library/recent-updates`,
          {
            itemId: id,
            title: itemData.libraryFileName || '',
            description: updateDescription,
            updatedAt: now
          }
        ).catch((err) => handleErrors('ContentLibraryService.updateRecentUpdatesCache', err, { sendToSentry: false }))
      }

      return { id, ...itemData }
    } catch (error) {
      handleErrors('ContentLibraryService.updateItem', error)
      throw error
    }
  }

  const deleteItem = async (id) => {
    try {
      let documentNotFoundInStorage = false
      const currentItem = await getItem(id)

      const storagePath = currentItem ? getItemStoragePath(currentItem) : ''
      if (storagePath) {
        const deleteResult = await deleteDocument(storagePath, { ignoreNotFound: true })
        documentNotFoundInStorage = Boolean(deleteResult?.notFound)
      }

      await remove(ref(db, `CONTENT_LIBRARY/ITEMS/${id}`))

      return { success: true, documentNotFoundInStorage }
    } catch (error) {
      handleErrors('ContentLibraryService.deleteItem', error)
      throw error
    }
  }

  // Resolves the Storage path for any item that has a `documentFileName`.
  // Returns '' for items without a file attached, so callers can guard a
  // no-op delete.
  const getItemStoragePath = (item) => {
    if (!item || !item.documentFileName) {
      return ''
    }
    return item.brandingEngine
      ? getContentLibraryTemplatePath(item.documentFileName)
      : getContentLibraryOriginalPath(item.documentFileName)
  }

  // ============= CATEGORIES =============
  const getCategories = async () => {
    try {
      const snapshot = await get(ref(db, 'CONTENT_LIBRARY/CATEGORIES'))
      return mapSnapshotToArray(snapshot)
    } catch (error) {
      handleErrors('ContentLibraryService.getCategories', error)
      throw error
    }
  }

  const getCategory = async (id) => {
    try {
      const snapshot = await get(ref(db, `CONTENT_LIBRARY/CATEGORIES/${id}`))
      const category = snapshot.val()
      return category ? { id, ...category } : null
    } catch (error) {
      handleErrors('ContentLibraryService.getCategory', error)
      throw error
    }
  }

  const getTaxonomyGroups = async ({ activeOnly = false } = {}) => {
    try {
      const snapshot = await get(ref(db, 'CONTENT_LIBRARY/TAXONOMY_GROUPS'))
      const taxonomyGroups = mapSnapshotToArray(snapshot)
        .filter((group) => (activeOnly ? group.isActive !== false : true))
        .sort((firstGroup, secondGroup) => {
          const firstSortOrder = Number(firstGroup.sortOrder ?? Number.MAX_SAFE_INTEGER)
          const secondSortOrder = Number(secondGroup.sortOrder ?? Number.MAX_SAFE_INTEGER)
          return firstSortOrder - secondSortOrder
        })

      return taxonomyGroups
    } catch (error) {
      handleErrors('ContentLibraryService.getTaxonomyGroups', error)
      throw error
    }
  }

  const getTaxonomyGroupDetails = async (taxonomyGroupId) => {
    try {
      if (!taxonomyGroupId) {
        return null
      }

      const snapshot = await get(ref(db, `CONTENT_LIBRARY/TAXONOMY_GROUPS/${taxonomyGroupId}`))
      const taxonomyGroup = snapshot.val()
      return taxonomyGroup ? { id: taxonomyGroupId, ...taxonomyGroup } : null
    } catch (error) {
      handleErrors('ContentLibraryService.getTaxonomyGroupDetails', error)
      throw error
    }
  }

  const getCategoriesByTaxonomyGroup = async (taxonomyGroupId) => {
    try {
      const categories = await getCategories()
      return categories.filter((category) => category.taxonomyGroupId === taxonomyGroupId)
    } catch (error) {
      handleErrors('ContentLibraryService.getCategoriesByTaxonomyGroup', error)
      throw error
    }
  }

  const getItemsByTaxonomyGroup = async (taxonomyGroupId) => {
    try {
      const [items, categories] = await Promise.all([
        getItems(),
        getCategories()
      ])
      const categoryIds = new Set(
        categories
          .filter((category) => category.taxonomyGroupId === taxonomyGroupId)
          .map((category) => category.id)
      )
      return items.filter((item) => categoryIds.has(item.libraryCategoryId))
    } catch (error) {
      handleErrors('ContentLibraryService.getItemsByTaxonomyGroup', error)
      throw error
    }
  }

  const getGroupedItemsByTaxonomyGroup = async (taxonomyGroupId) => {
    try {
      const [items, categories, details] = await Promise.all([
        getItems(),
        getCategories(),
        getTaxonomyGroupDetails(taxonomyGroupId)
      ])
      const groupedCategories = categories
        .filter((category) => category.taxonomyGroupId === taxonomyGroupId)
        .map((category) => ({
          ...category,
          items: items.filter((item) => item.libraryCategoryId === category.id)
        }))
      return {
        taxonomyGroupId,
        details,
        categories: groupedCategories
      }
    } catch (error) {
      handleErrors('ContentLibraryService.getGroupedItemsByTaxonomyGroup', error)
      throw error
    }
  }

  const getFavoriteItemIds = async () => {
    try {
      const favoritesCollectionRef = getFavoritesCollectionRef()
      if (!favoritesCollectionRef) {
        return []
      }

      const snapshot = await getDocs(favoritesCollectionRef)
      return snapshot.docs.map((favoriteDoc) => favoriteDoc.id)
    } catch (error) {
      handleErrors('ContentLibraryService.getFavoriteItemIds', error)
      throw error
    }
  }

  const addFavoriteItem = async (item) => {
    try {
      const itemId = typeof item === 'string' ? item : item?.id
      const userUid = getCurrentUserUid()
      if (!userUid || !itemId) {
        return false
      }

      await setDoc(doc(firestore, 'users', userUid, 'contentLibraryFavorites', itemId), {
        itemId,
        libraryFileName: item?.libraryFileName || '',
        taxonomyGroupId: item?.taxonomyGroupId || '',
        created_at: Timestamp.fromDate(new Date())
      })

      return true
    } catch (error) {
      handleErrors('ContentLibraryService.addFavoriteItem', error)
      throw error
    }
  }

  const removeFavoriteItem = async (itemId) => {
    try {
      const userUid = getCurrentUserUid()
      if (!userUid || !itemId) {
        return false
      }

      await deleteDoc(doc(firestore, 'users', userUid, 'contentLibraryFavorites', itemId))

      return true
    } catch (error) {
      handleErrors('ContentLibraryService.removeFavoriteItem', error)
      throw error
    }
  }

  const getDownloadStatsByItemId = async () => {
    try {
      const downloadsCollectionRef = getDownloadsCollectionRef()
      if (!downloadsCollectionRef) {
        return new Map()
      }

      const snapshot = await getDocs(downloadsCollectionRef)
      return new Map(snapshot.docs.map((downloadDoc) => [downloadDoc.id, downloadDoc.data()]))
    } catch (error) {
      handleErrors('ContentLibraryService.getDownloadStatsByItemId', error)
      throw error
    }
  }

  // Streams a non-brandable Content Library document through the backend so the
  // file is always served from the current environment's bucket. Mirrors the
  // pattern used by ContentLibraryTemplateService.downloadCachedContentLibraryDocument.
  const downloadDocument = async (item) => {
    if (!item?.id) {
      throw new Error('missing_item_id')
    }
    if (item.brandingEngine) {
      throw new Error('item_is_brandable_template')
    }
    if (!item.documentFileName) {
      throw new Error('item_missing_document_filename')
    }

    const response = await post(
      'contentLibraryDocumentDownload',
      `${functionsBaseUrl}/utilityGateway/api/v1/content-library/document-download`,
      { itemId: item.id },
      { responseType: 'arraybuffer' }
    )

    const fileName = buildContentLibraryDownloadFileName(item)
    const dotIndex = fileName.lastIndexOf('.')
    const extension = dotIndex >= 0 ? fileName.slice(dotIndex + 1).toLowerCase() : ''
    const contentType = DOCUMENT_DOWNLOAD_CONTENT_TYPES[extension] || 'application/octet-stream'

    const blob = new Blob([response.data], { type: contentType })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  const getCurrentWeekMondayInBogota = () => {
    const now = new Date()
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: BOGOTA_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(now)
    const year = Number(parts.find((p) => p.type === 'year').value)
    const month = Number(parts.find((p) => p.type === 'month').value) - 1
    const day = Number(parts.find((p) => p.type === 'day').value)
    const bogotaToday = new Date(Date.UTC(year, month, day))
    const dayOfWeek = bogotaToday.getUTCDay()
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
    bogotaToday.setUTCDate(bogotaToday.getUTCDate() - daysToMonday)
    return bogotaToday.toISOString().split('T')[0]
  }

  const recordItemDownload = async (item, format = '') => {
    try {
      const userUid = getCurrentUserUid()
      if (!userUid || !item?.id) {
        return null
      }

      const downloadDocRef = doc(firestore, 'users', userUid, 'contentLibraryDownloads', item.id)
      // Timestamp.fromDate, not serverTimestamp(): this payload is returned to the
      // caller and pushed straight into `downloadStatsByItemId`, which the sort
      // comparators read. A serverTimestamp() sentinel has no date to compare.
      const now = Timestamp.fromDate(new Date())

      const nextDownloadData = await runTransaction(firestore, async (transaction) => {
        const existingDownloadDoc = await transaction.get(downloadDocRef)
        const existingDownloadData = existingDownloadDoc.exists() ? existingDownloadDoc.data() : null

        const currentWeekStart = getCurrentWeekMondayInBogota()
        const existingWeeklyStats = existingDownloadData?.weeklyStats
        const weeklyCount =
          existingWeeklyStats?.weekStart === currentWeekStart
            ? (existingWeeklyStats.count || 0) + 1
            : 1

        const payload = {
          itemId: item.id,
          taxonomyGroupId: item.taxonomyGroupId || '',
          libraryCategoryId: item.libraryCategoryId || '',
          libraryFileName: item.libraryFileName || '',
          documentDisplayName: item.documentDisplayName || '',
          documentFileName: item.documentFileName || '',
          downloadedFormat: format || existingDownloadData?.downloadedFormat || '',
          firstDownloadedAt: toFirestoreTimestamp(existingDownloadData?.firstDownloadedAt) || now,
          lastDownloadedAt: now,
          downloadCount: Number(existingDownloadData?.downloadCount || 0) + 1,
          weeklyStats: {
            weekStart: currentWeekStart,
            count: weeklyCount
          },
          updated_at: now
        }

        transaction.set(downloadDocRef, payload)
        return payload
      })

      return nextDownloadData
    } catch (error) {
      handleErrors('ContentLibraryService.recordItemDownload', error)
      throw error
    }
  }

  const createCategory = async (categoryData) => {
    try {
      // Validation
      if (!categoryData.name) {
        throw new Error('Category name is required')
      }
      
      // Save to Realtime Database
      const newRef = push(ref(db, 'CONTENT_LIBRARY/CATEGORIES'))
      await set(newRef, {
        ...categoryData,
        created_at: new Date().toISOString(),
        created_by: localStorage.getItem('userUid')
      })
      
      return { id: newRef.key, ...categoryData }
    } catch (error) {
      handleErrors('ContentLibraryService.createCategory', error)
      throw error
    }
  }

  const updateCategory = async (id, categoryData) => {
    try {
      // Validation
      if (!categoryData.name) {
        throw new Error('Category name is required')
      }
      
      // Update in Realtime Database
      await update(ref(db, `CONTENT_LIBRARY/CATEGORIES/${id}`), {
        ...categoryData,
        updated_at: new Date().toISOString(),
        updated_by: localStorage.getItem('userUid')
      })
      
      return { id, ...categoryData }
    } catch (error) {
      handleErrors('ContentLibraryService.updateCategory', error)
      throw error
    }
  }

  const deleteCategory = async (id) => {
    try {
      // Optimized: Only query items with this specific categoryId
      const itemsQuery = query(
        ref(db, 'CONTENT_LIBRARY/ITEMS'),
        orderByChild('libraryCategoryId'),
        equalTo(id)
      )
      const itemsSnapshot = await get(itemsQuery)
      const hasAssociatedItems = itemsSnapshot.exists()
      
      if (hasAssociatedItems) {
        return { 
          success: false, 
          error: 'Cannot delete category because it has associated items',
          errorType: 'associated_items'
        }
      }
      
      // Delete category from Database
      await remove(ref(db, `CONTENT_LIBRARY/CATEGORIES/${id}`))
      
      return { success: true }
    } catch (error) {
      handleErrors('ContentLibraryService.deleteCategory', error, { logToConsole: false })
      return { success: false, error: 'Error deleting category' }
    }
  }

  // ============= REALTIME LISTENERS (Optional) =============
  const subscribeToItems = (callback) => {
    const itemsRef = ref(db, 'CONTENT_LIBRARY/ITEMS')
    return onValue(itemsRef, (snapshot) => {
      const items = snapshot.val() || {}
      const itemsArray = Object.entries(items).map(([id, data]) => ({ id, ...data }))
      callback(itemsArray)
    })
  }

  const subscribeToCategories = (callback) => {
    const categoriesRef = ref(db, 'CONTENT_LIBRARY/CATEGORIES')
    return onValue(categoriesRef, (snapshot) => {
      callback(mapSnapshotToArray(snapshot))
    })
  }

  return {
    getTaxonomyGroups,
    // Items
    getItems,
    getItem,
    createItem,
    updateItem,
    deleteItem,
    getItemsByTaxonomyGroup,
    getGroupedItemsByTaxonomyGroup,
    getCurrentLibraryActivityContext,
    recordLibrarySearch,
    recordLastLibraryActivity,
    getAgencyLatestLibraryActivities,
    getFavoriteItemIds,
    addFavoriteItem,
    removeFavoriteItem,
    getDownloadStatsByItemId,
    recordItemDownload,
    downloadDocument,
    subscribeToItems,
    
    // Categories
    getCategories,
    getCategory,
    getTaxonomyGroupDetails,
    getCategoriesByTaxonomyGroup,
    createCategory,
    updateCategory,
    deleteCategory,
    subscribeToCategories
  }
}